import React from 'react'
import { Link, useParams } from 'react-router-dom'
import { tutorialsByGroup, findTutorial } from '../guide/tutorials'
import { C, ui } from '../theme'

const PUBLIC = process.env.PUBLIC_URL || ''

// ── live UI "chips" that mirror the real controls so steps never go stale ─────
const CHIP_STYLE = {
  btn: { background: C.gold, color: C.paperRaised, border: `1px solid ${C.gold}` },
  ghost: { background: C.paperRaised, color: C.ink, border: `1px solid ${C.rule}` },
  tab: { background: C.paperSunken, color: C.ink, border: `1px solid ${C.ruleStrong}` },
  select: { background: C.paperSunken, color: C.inkSoft, border: `1px solid ${C.rule}` },
  input: { background: C.paperSunken, color: C.inkSoft, border: `1px dashed ${C.rule}` },
  key: { background: 'transparent', color: C.inkSoft, border: `1px solid ${C.inkFaint}` },
}
function Chip({ label, kind = 'btn' }) {
  const s = CHIP_STYLE[kind] || CHIP_STYLE.btn
  return (
    <span style={{
      ...s, display: 'inline-block', padding: '1px 7px', borderRadius: 4, margin: '0 1px',
      fontSize: 12.5, fontFamily: kind === 'btn' || kind === 'tab' ? "'Cinzel', serif" : 'inherit',
      letterSpacing: kind === 'btn' || kind === 'tab' ? 0.3 : 0, whiteSpace: 'nowrap', verticalAlign: 'baseline',
    }}>{label}</span>
  )
}

// Render step text with {0},{1},… replaced inline by chips; leftover chips trail.
function StepText({ text, chips = [] }) {
  const parts = String(text).split(/(\{\d+\})/g)
  const used = new Set()
  const out = parts.map((p, i) => {
    const m = p.match(/^\{(\d+)\}$/)
    if (m && chips[+m[1]]) { used.add(+m[1]); return <Chip key={i} {...chips[+m[1]]} /> }
    return <span key={i}>{p}</span>
  })
  const trailing = chips.filter((_, i) => !used.has(i))
  return (
    <>
      {out}
      {trailing.length > 0 && (
        <span style={{ marginLeft: 6 }}>{trailing.map((c, i) => <Chip key={i} {...c} />)}</span>
      )}
    </>
  )
}

const ROLE_LABEL = { dm: 'DM', player: 'Player', all: 'Everyone' }
function RoleBadges({ roles = [] }) {
  return (
    <span style={{ display: 'inline-flex', gap: 6 }}>
      {roles.map(r => (
        <span key={r} style={{
          fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 1, padding: '1px 7px', borderRadius: 10,
          background: r === 'dm' ? C.gold : C.paperSunken, color: r === 'dm' ? C.paperRaised : C.inkSoft,
          border: `1px solid ${r === 'dm' ? C.gold : C.rule}`,
        }}>{ROLE_LABEL[r] || r}</span>
      ))}
    </span>
  )
}

function Overview() {
  const groups = tutorialsByGroup()
  return (
    <div>
      <h1 style={{ fontFamily: "'Cinzel', serif", fontSize: 24, letterSpacing: 0.5, margin: '0 0 6px' }}>Field Guide</h1>
      <p style={{ color: C.inkSoft, fontSize: 14.5, lineHeight: 1.55, margin: '0 0 18px', maxWidth: 640 }}>
        Step-by-step walkthroughs of every workflow in the campaign tool. Pick a topic from the
        index, or browse the catalogue below. Controls shown like <Chip label="⚔ Recruit" kind="ghost" />{' '}
        mirror the real buttons.
      </p>
      {groups.map(g => (
        <div key={g.id} style={{ marginBottom: 16 }}>
          <div style={ui.h}>{g.title}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {g.items.map(t => (
              <Link key={t.slug} to={`/guide/${t.slug}`}
                style={{ ...ui.card, textDecoration: 'none', color: C.ink, padding: '9px 13px', background: C.paperRaised, minWidth: 200 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{t.title}</div>
                <div style={{ color: C.inkFaint, fontSize: 12, marginTop: 2 }}>{t.intro.slice(0, 70)}…</div>
              </Link>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

function Article({ t }) {
  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
        <h1 style={{ fontFamily: "'Cinzel', serif", fontSize: 23, letterSpacing: 0.5, margin: 0 }}>{t.title}</h1>
        <RoleBadges roles={t.roles} />
      </div>
      <p style={{ color: C.inkSoft, fontSize: 14.5, lineHeight: 1.6, margin: '0 0 18px' }}>{t.intro}</p>

      {t.shot && (
        <img src={`${PUBLIC}/guide/${t.shot}`} alt={`${t.title} screenshot`}
          style={{ maxWidth: '100%', borderRadius: 6, border: `1px solid ${C.ruleStrong}`, boxShadow: `0 2px 8px ${C.shadow}`, marginBottom: 20 }} />
      )}

      <ol style={{ listStyle: 'none', counterReset: 'step', padding: 0, margin: 0 }}>
        {t.steps.map((s, i) => (
          <li key={i} style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
            <span style={{
              flex: '0 0 26px', width: 26, height: 26, borderRadius: '50%', display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center', fontFamily: "'Cinzel', serif", fontSize: 13,
              background: C.paperSunken, color: C.gold, border: `1px solid ${C.ruleStrong}`,
            }}>{i + 1}</span>
            <div style={{ fontSize: 14.5, lineHeight: 1.6, color: C.ink, paddingTop: 2 }}>
              <StepText text={s.text} chips={s.chips} />
              {s.note && (
                <div style={{ marginTop: 5, fontSize: 13, color: C.inkSoft, fontStyle: 'italic',
                  borderLeft: `2px solid ${C.rule}`, paddingLeft: 9 }}>{s.note}</div>
              )}
            </div>
          </li>
        ))}
      </ol>
    </div>
  )
}

export default function Guide() {
  const { slug } = useParams()
  const groups = tutorialsByGroup()
  const t = slug ? findTutorial(slug) : null

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: C.paper, color: C.ink }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '9px 18px',
        borderBottom: `1px solid ${C.ruleStrong}`, background: C.paperRail, boxShadow: `0 1px 0 ${C.rule}` }}>
        <Link to="/" style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: 17, letterSpacing: 1, color: C.ink, textDecoration: 'none' }}>TACITVS</Link>
        <strong style={{ fontSize: 16, color: C.inkSoft, fontWeight: 600 }}>Field Guide</strong>
        <Link to="/" style={{ marginLeft: 'auto', fontSize: 13, color: C.gold, textDecoration: 'none',
          padding: '4px 12px', borderRadius: 4, border: `1px solid ${C.rule}`, background: C.paperRaised }}>← Back to campaigns</Link>
      </header>

      <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        <aside style={{ width: 256, flex: '0 0 256px', background: C.paperRail, borderRight: `1px solid ${C.ruleStrong}`, padding: '8px 12px', overflowY: 'auto' }}>
          <Link to="/guide" style={{ display: 'block', textDecoration: 'none', color: !t ? C.gold : C.inkSoft,
            fontSize: 13, fontWeight: !t ? 700 : 500, padding: '6px 8px' }}>Overview</Link>
          {groups.map(g => (
            <div key={g.id}>
              <div style={{ ...ui.h, margin: '14px 0 4px' }}>{g.title}</div>
              {g.items.map(it => {
                const active = it.slug === slug
                return (
                  <Link key={it.slug} to={`/guide/${it.slug}`}
                    style={{ display: 'block', textDecoration: 'none', fontSize: 13.5, lineHeight: 1.35,
                      padding: '5px 8px', borderRadius: 4, marginBottom: 1,
                      color: active ? C.paperRaised : C.ink, background: active ? C.gold : 'transparent',
                      fontWeight: active ? 600 : 400 }}>{it.title}</Link>
                )
              })}
            </div>
          ))}
        </aside>

        <main style={{ flex: 1, minWidth: 0, overflowY: 'auto', padding: '24px 32px' }}>
          {t ? <Article t={t} /> : <Overview />}
        </main>
      </div>
    </div>
  )
}
